import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Card, Button, Badge, Modal, Form, InputGroup } from "react-bootstrap";
import { FaShoppingCart, FaCheckCircle, FaSearch, FaPlus, FaMinus, FaTrash, FaClipboardList, FaCoffee } from "react-icons/fa";
import { getAllProductsApi, getAllProductCategoriesApi, getModifiersByGroupApi, getProductModifierGroupsApi, createOrderApi, getAllDiscountsApi } from "../menu/service/api_service";
import Swal from "sweetalert2";
import CreatableSelect from "react-select/creatable";
import ImageViewerButton from "../layout/ImageViewerButton";

export default function Order() {
  const token = localStorage.getItem("token");
  
  // Data State
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Discounts Data
  const [masterDiscounts, setMasterDiscounts] = useState([]);
  const [appliedDiscounts, setAppliedDiscounts] = useState([]);
  
  // Cart State
  const [cart, setCart] = useState([]);
  
  // Modifiers State
  const [showModModal, setShowModModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [productModifiers, setProductModifiers] = useState([]); // Array of groups with items
  const [selectedModifiers, setSelectedModifiers] = useState({}); // { groupId: modifierId }
  const [modQuantity, setModQuantity] = useState(1);
  
  // Checkout State
  const [showCheckout, setShowCheckout] = useState(false);
  const now = new Date();
  let initialMin = Math.round(now.getMinutes() / 15) * 15;
  let initialHour = now.getHours();
  if (initialMin === 60) {
    initialMin = 0;
    initialHour = (initialHour + 1) % 24;
  }
  
  const [checkoutData, setCheckoutData] = useState({
    recipient_name: "",
    payment_method: "transfer",
    payment_status: "paid",
    slip_image_url: "",
    custom_date: now.toISOString().split('T')[0],
    custom_hour: initialHour.toString().padStart(2, '0'),
    custom_minute: initialMin.toString().padStart(2, '0')
  });
  const [slipFile, setSlipFile] = useState(null);
  const [slipPreviewUrl, setSlipPreviewUrl] = useState('');
  // UI State
  const [loading, setLoading] = useState(true);
  const primaryColor = "#1e3c72";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch Products & Categories & Discounts (active only for POS)
      const resProd = await getAllProductsApi(token, true);
      const resCat = await getAllProductCategoriesApi(token);
      const resDisc = await getAllDiscountsApi(token, true);
      
      setProducts(resProd.data || []);
      setFilteredProducts(resProd.data || []);
      setCategories(resCat.data || []);
      setMasterDiscounts(resDisc.data || []);
      
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let result = products;
    if (selectedCategory !== "All") {
      result = result.filter(p => p.category_name === selectedCategory);
    }
    if (searchQuery) {
      result = result.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    setFilteredProducts(result);
  }, [selectedCategory, searchQuery, products]);

  // Click Product Handler
  const handleProductClick = async (product) => {
    try {
      // Fetch modifier groups mapped to this product
      const resGroups = await getProductModifierGroupsApi(token, product.id, true);
      const groups = resGroups.data || [];
      
      if (groups.length === 0) {
        // No modifiers, add directly to cart
        addToCart(product, [], 1);
        return;
      }

      // Fetch items for each group
      const groupsWithItems = [];
      const defaultSelections = {};

      for (const group of groups) {
        const resItems = await getModifiersByGroupApi(token, group.id, true);
        const items = resItems.data || [];
        groupsWithItems.push({ ...group, items });
        
        // Find defaults based on max_choices
        const maxChoices = group.max_choices || 1;
        const defaultItems = items.filter(i => i.is_default).slice(0, maxChoices);
        
        if (defaultItems.length > 0) {
          defaultSelections[group.id] = defaultItems;
        } else if (group.is_required && items.length > 0) {
          defaultSelections[group.id] = [items[0]];
        } else {
          defaultSelections[group.id] = [];
        }
      }

      setProductModifiers(groupsWithItems);
      setSelectedModifiers(defaultSelections);
      setCurrentProduct(product);
      setModQuantity(1);
      setShowModModal(true);
      
    } catch (err) {
      console.error("Error fetching modifiers", err);
      Swal.fire("Error", "Could not load modifiers", "error");
    }
  };

  const handleModifierSelect = (group, item) => {
    setSelectedModifiers(prev => {
      const currentSelected = prev[group.id] || [];
      const maxChoices = group.max_choices || 1;
      
      const isAlreadySelected = currentSelected.some(m => m.id === item.id);
      
      if (isAlreadySelected) {
        // Deselect
        return { ...prev, [group.id]: currentSelected.filter(m => m.id !== item.id) };
      } else {
        // Select
        if (maxChoices === 1) {
          // Replace
          return { ...prev, [group.id]: [item] };
        } else {
          // Add if under max
          if (currentSelected.length < maxChoices) {
            return { ...prev, [group.id]: [...currentSelected, item] };
          } else {
            // Already at max, maybe alert? Or just ignore
            Swal.fire("Limit Reached", `You can only select up to ${maxChoices} options for ${group.name}`, "info");
            return prev;
          }
        }
      }
    });
  };

  const addToCartFromModal = () => {
    // Validate required groups
    for (const group of productModifiers) {
      const selectedCount = (selectedModifiers[group.id] || []).length;
      if (group.is_required && selectedCount === 0) {
        Swal.fire("Warning", `Please select an option for ${group.name}`, "warning");
        return;
      }
    }
    
    // Flatten all selected items into a single array
    const mods = [];
    Object.values(selectedModifiers).forEach(groupSelected => {
      if (Array.isArray(groupSelected)) {
        mods.push(...groupSelected);
      }
    });
    addToCart(currentProduct, mods, modQuantity);
    setShowModModal(false);
  };

  const addToCart = (product, modifiers = [], qty = 1) => {
    // Check if identical item exists (same product + same modifiers)
    const modifierIds = modifiers.map(m => m.id).sort().join(",");
    
    const existingIndex = cart.findIndex(item => 
      item.product.id === product.id && 
      item.modifiers.map(m => m.id).sort().join(",") === modifierIds
    );

    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += qty;
      setCart(newCart);
    } else {
      setCart([...cart, { product, modifiers, quantity: qty }]);
    }
  };

  const updateCartQuantity = (index, delta) => {
    const newCart = [...cart];
    const newQty = newCart[index].quantity + delta;
    if (newQty <= 0) {
      newCart.splice(index, 1);
    } else {
      newCart[index].quantity = newQty;
    }
    setCart(newCart);
  };

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => {
      let itemPrice = Number(item.product.base_price);
      item.modifiers.forEach(m => itemPrice += Number(m.price_adjust));
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  const getNetDiscount = (disc, subtotal) => {
    if (disc.is_override) return Number(disc.net_value || 0);
    if (disc.type === 'percentage') {
      return (subtotal * Number(disc.value || 0)) / 100;
    }
    return Number(disc.value || 0);
  };

  const calculateDiscount = (subtotal) => {
    let totalDiscount = 0;
    appliedDiscounts.forEach(d => {
      totalDiscount += getNetDiscount(d, subtotal);
    });
    return totalDiscount;
  };

  const handleAddDiscountRow = () => {
    setAppliedDiscounts([...appliedDiscounts, { name: '', type: 'amount', value: '', isFromMaster: false, is_override: false, net_value: '' }]);
  };

  const handleRemoveDiscountRow = (idx) => {
    setAppliedDiscounts(appliedDiscounts.filter((_, i) => i !== idx));
  };

  const handleDiscountChange = (idx, field, value) => {
    const newDiscounts = [...appliedDiscounts];
    newDiscounts[idx][field] = value;
    
    // Auto-fill if name matches master data
    if (field === 'name') {
      const match = masterDiscounts.find(m => m.name === value);
      if (match) {
        newDiscounts[idx].type = match.type;
        newDiscounts[idx].value = match.value;
        newDiscounts[idx].isFromMaster = true;
        newDiscounts[idx].is_override = false;
      } else {
        newDiscounts[idx].isFromMaster = false;
      }
    }
    
    setAppliedDiscounts(newDiscounts);
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (cart.length === 0) return;

    try {
      let deliveryTimeStr = null;
      if (checkoutData.custom_date && checkoutData.custom_hour && checkoutData.custom_minute) {
        const customDt = new Date(`${checkoutData.custom_date}T${checkoutData.custom_hour.padStart(2, '0')}:${checkoutData.custom_minute}:00`);
        deliveryTimeStr = customDt.toISOString();
      }

      const orderPayload = {
        recipient_name: checkoutData.recipient_name,
        payment_method: checkoutData.payment_method,
        payment_status: checkoutData.payment_status,
        slip_image_url: checkoutData.slip_image_url,
        delivery_time: deliveryTimeStr,
        items: cart.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
          price_at_sale: item.product.base_price,
          modifiers: item.modifiers.map(m => ({
            modifier_id: m.id,
            price_at_sale: m.price_adjust
          }))
        })),
        discounts: appliedDiscounts.filter(d => d.name && (d.value || d.is_override)).map(d => {
          if (d.is_override || !d.isFromMaster) {
            return { name: d.name, type: 'amount', value: getNetDiscount(d, calculateSubtotal()) };
          }
          return { name: d.name, type: d.type, value: d.value };
        })
      };

      const formData = new FormData();
      formData.append("orderData", JSON.stringify(orderPayload));
      if (slipFile) {
        formData.append("slip", slipFile);
      }

      await createOrderApi(token, formData);
      
      Swal.fire("Success", "Order created successfully!", "success");
      setCart([]);
      setAppliedDiscounts([]);
      setShowCheckout(false);
      setSlipFile(null);
      const d = new Date();
      let m = Math.round(d.getMinutes() / 15) * 15;
      let h = d.getHours();
      if (m === 60) {
        m = 0;
        h = (h + 1) % 24;
      }
      setCheckoutData({ 
        recipient_name: "", 
        payment_method: "transfer", 
        payment_status: "paid",
        slip_image_url: "",
        custom_date: d.toISOString().split('T')[0],
        custom_hour: h.toString().padStart(2, '0'),
        custom_minute: m.toString().padStart(2, '0')
      });
      
    } catch (err) {
      console.error(err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message;
      if (errorMsg && errorMsg.includes("INSUFFICIENT_STOCK")) {
        const cleanMsg = errorMsg.replace("INSUFFICIENT_STOCK:", "").trim();
        Swal.fire({
          icon: "error",
          title: "สต๊อกไม่เพียงพอ!",
          text: cleanMsg,
          confirmButtonColor: "#d33"
        });
      } else {
        Swal.fire("Error", errorMsg || "Failed to create order", "error");
      }
    }
  };

  const setShortcutTime = (type) => {
    const d = new Date();
    if (type === "now") {
      let m = Math.round(d.getMinutes() / 15) * 15;
      let h = d.getHours();
      if (m === 60) {
        m = 0;
        h = (h + 1) % 24;
      }
      setCheckoutData(prev => ({
        ...prev,
        custom_date: d.toISOString().split('T')[0],
        custom_hour: h.toString().padStart(2, '0'),
        custom_minute: m.toString().padStart(2, '0')
      }));
    } else if (type.startsWith("tomorrow_")) {
      d.setDate(d.getDate() + 1);
      const timeParts = type.split("_")[1];
      const hours = timeParts.length === 3 ? timeParts[0] : timeParts.substring(0,2);
      const minutes = timeParts.length === 3 ? timeParts.substring(1) : timeParts.substring(2);
      setCheckoutData(prev => ({
        ...prev,
        custom_date: d.toISOString().split('T')[0],
        custom_hour: hours.toString().padStart(2, '0'),
        custom_minute: minutes
      }));
    }
  };

  if (loading) return <div className="p-4 text-center">Loading POS...</div>;

  return (
    <>
      <style>{`
        .pos-container {
          height: calc(100vh - 50px) !important;
          overflow: hidden;
        }
        @media (max-width: 767px) {
          .pos-container {
            height: auto !important;
            overflow: visible;
          }
        }
        .pos-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .pos-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .pos-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .pos-scroll::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
      <Container fluid className="px-3 py-3 pos-container" style={{ backgroundColor: "#f4f7fa" }}>
        <Row className="h-100">
        
        {/* Left Side: Product Grid */}
        <Col lg={8} md={7} className="mb-4 mb-md-0 d-flex flex-column h-100">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="m-0 fw-bold" style={{ color: primaryColor }}>
              <FaClipboardList className="me-2" /> New Order
            </h4>
            <div style={{ width: "250px" }}>
              <InputGroup>
                <InputGroup.Text className="bg-white border-end-0 text-muted"><FaSearch /></InputGroup.Text>
                <Form.Control 
                  placeholder="Search menu..." 
                  className="border-start-0 ps-0 shadow-none"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
            </div>
          </div>
          
          <div className="mb-3 overflow-auto" style={{ whiteSpace: "nowrap" }}>
            <Button 
              variant={selectedCategory === "All" ? "primary" : "outline-secondary"} 
              className="me-2 rounded-pill px-4 border-0 shadow-sm"
              style={selectedCategory === "All" ? { backgroundColor: primaryColor } : {}}
              onClick={() => setSelectedCategory("All")}
            >
              All Menus
            </Button>
            {categories.map(cat => (
              <Button 
                key={cat.id}
                variant={selectedCategory === cat.name ? "primary" : "outline-secondary"} 
                className="me-2 rounded-pill px-4 border-0 shadow-sm"
                style={selectedCategory === cat.name ? { backgroundColor: primaryColor } : {}}
                onClick={() => setSelectedCategory(cat.name)}
              >
                {cat.name}
              </Button>
            ))}
          </div>

          <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }} className="pb-4 pos-scroll">
            <div className="row g-3">
              {filteredProducts.map(product => (
                <Col xl={3} lg={4} sm={6} xs={6} key={product.id}>
                  <Card 
                    className="h-100 border-0 shadow-sm product-card" 
                    style={{ cursor: "pointer", transition: "transform 0.2s" }}
                    onClick={() => handleProductClick(product)}
                    onMouseOver={(e) => e.currentTarget.style.transform = "scale(1.03)"}
                    onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"}
                  >
                    <div className="card-img-top bg-light d-flex align-items-center justify-content-center text-muted" style={{ height: "120px", fontSize: "2rem" }}>
                      <FaCoffee />
                    </div>
                    <Card.Body className="p-3 d-flex flex-column justify-content-between">
                      <h6 className="fw-bold mb-1 text-truncate" title={product.name}>{product.name}</h6>
                      <div className="text-primary fw-bold">฿{Number(product.base_price).toLocaleString()}</div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </div>
          </div>
        </Col>

        {/* Right Side: Cart */}
        <Col lg={4} md={5} className="h-100">
          <Card className="border-0 shadow-sm h-100 d-flex flex-column rounded-4 overflow-hidden">
            <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
              <h5 className="m-0 fw-bold" style={{ color: primaryColor }}><FaShoppingCart className="me-2"/> Cart</h5>
              <Badge bg="primary" pill style={{ backgroundColor: primaryColor }}>{cart.length} Items</Badge>
            </Card.Header>
            
            <Card.Body className="overflow-auto p-0 flex-grow-1 pos-scroll" style={{ backgroundColor: "#f8f9fa" }}>
              {cart.length === 0 ? (
                <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted opacity-50">
                  <FaShoppingCart size={48} className="mb-3" />
                  <p>Your cart is empty</p>
                </div>
              ) : (
                <div className="p-3">
                  {cart.map((item, index) => {
                    let itemUnitTotal = Number(item.product.base_price);
                    item.modifiers.forEach(m => itemUnitTotal += Number(m.price_adjust));
                    
                    return (
                      <Card key={index} className="mb-2 border-0 shadow-sm rounded-3">
                        <Card.Body className="p-2 px-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <div className="fw-bold text-dark">{item.product.name}</div>
                              {item.modifiers.length > 0 && (
                                <div className="small text-muted" style={{ fontSize: "0.75rem" }}>
                                  {item.modifiers.map(m => m.name).join(", ")}
                                </div>
                              )}
                            </div>
                            <div className="fw-bold text-primary">
                              ฿{(itemUnitTotal * item.quantity).toLocaleString()}
                            </div>
                          </div>
                          
                          <div className="d-flex justify-content-between align-items-center mt-2">
                            <div className="d-flex align-items-center gap-2">
                              <Button variant="light" size="sm" className="rounded-circle p-1 d-flex align-items-center justify-content-center text-primary" style={{ width: '28px', height: '28px' }} onClick={() => updateCartQuantity(index, -1)}>
                                <FaMinus size={10} />
                              </Button>
                              <span className="fw-bold">{item.quantity}</span>
                              <Button variant="light" size="sm" className="rounded-circle p-1 d-flex align-items-center justify-content-center text-primary" style={{ width: '28px', height: '28px' }} onClick={() => updateCartQuantity(index, 1)}>
                                <FaPlus size={10} />
                              </Button>
                            </div>
                            <Button variant="link" className="text-danger p-0 shadow-none text-decoration-none" onClick={() => updateCartQuantity(index, -item.quantity)}>
                              <FaTrash size={14} />
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              )}
            </Card.Body>
            
            {/* Cart Footer / Checkout */}
            <div className="bg-white p-3 border-top mt-auto shadow-sm">
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted fw-bold">Subtotal</span>
                <span className="fw-bold">฿{calculateSubtotal().toLocaleString()}</span>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <span className="text-muted fw-bold">Total</span>
                <span className="fs-4 fw-bold" style={{ color: primaryColor }}>฿{Math.max(0, calculateSubtotal()).toLocaleString()}</span>
              </div>
              <Button 
                variant="primary" 
                size="lg" 
                className="w-100 fw-bold border-0 py-3 shadow"
                style={{ backgroundColor: primaryColor }}
                disabled={cart.length === 0}
                onClick={() => setShowCheckout(true)}
              >
                Checkout <FaCheckCircle className="ms-2" />
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Modifier Modal */}
      <Modal show={showModModal} onHide={() => setShowModModal(false)} centered size="md">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold" style={{ color: primaryColor }}>{currentProduct?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4">
            {productModifiers.map(group => (
              <div key={group.id} className="mb-3">
                <h6 className="fw-bold mb-2">
                  {group.name} 
                  {group.is_required && <span className="text-danger ms-1">*</span>}
                  <small className="text-muted ms-2 fw-normal">(Max: {group.max_choices || 1})</small>
                </h6>
                <div className="d-flex flex-wrap gap-2">
                  {group.items.map(item => {
                    const isSelected = (selectedModifiers[group.id] || []).some(m => m.id === item.id);
                    return (
                      <Button
                        key={item.id}
                        variant={isSelected ? "primary" : "outline-secondary"}
                        className={`rounded-pill px-3 py-1 ${isSelected ? 'border-0' : ''}`}
                        style={isSelected ? { backgroundColor: primaryColor } : {}}
                        onClick={() => handleModifierSelect(group, item)}
                      >
                        {item.name} {Number(item.price_adjust) > 0 ? `(+฿${Number(item.price_adjust)})` : ''}
                      </Button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          
          <div className="d-flex justify-content-center align-items-center mb-3">
             <Button variant="light" className="rounded-circle p-2 shadow-sm text-primary" onClick={() => setModQuantity(Math.max(1, modQuantity - 1))}><FaMinus/></Button>
             <span className="mx-4 fs-5 fw-bold">{modQuantity}</span>
             <Button variant="light" className="rounded-circle p-2 shadow-sm text-primary" onClick={() => setModQuantity(modQuantity + 1)}><FaPlus/></Button>
          </div>
          
          <Button variant="primary" className="w-100 py-2 fw-bold border-0" style={{ backgroundColor: primaryColor }} onClick={addToCartFromModal}>
            Add to Cart
          </Button>
        </Modal.Body>
      </Modal>

      {/* Checkout Modal */}
      <Modal show={showCheckout} onHide={() => setShowCheckout(false)} centered size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold" style={{ color: primaryColor }}>Complete Order</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCheckout}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-muted small">Recipient Name / Room</Form.Label>
              <Form.Control 
                type="text" 
                placeholder="e.g. Room 101, Mr. Smith" 
                value={checkoutData.recipient_name}
                onChange={e => setCheckoutData({...checkoutData, recipient_name: e.target.value})}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-muted small d-block">Delivery / Pickup Time</Form.Label>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => setShortcutTime("now")}
                >Now</Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => setShortcutTime("tomorrow_800")}
                >Tomorrow 8:00</Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => setShortcutTime("tomorrow_830")}
                >Tomorrow 8:30</Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => setShortcutTime("tomorrow_900")}
                >Tomorrow 9:00</Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => setShortcutTime("tomorrow_1200")}
                >Tomorrow 12:00</Button>
              </div>

              <Row className="g-2">
                <Col xs={12} sm={6}>
                  <Form.Control 
                    type="date" 
                    value={checkoutData.custom_date}
                    onChange={e => setCheckoutData({...checkoutData, custom_date: e.target.value})}
                    required
                  />
                </Col>
                <Col xs={6} sm={3}>
                  <Form.Select 
                    value={checkoutData.custom_hour}
                    onChange={e => setCheckoutData({...checkoutData, custom_hour: e.target.value})}
                  >
                    {Array.from({length: 24}).map((_, h) => (
                      <option key={h} value={h.toString().padStart(2, '0')}>{h.toString().padStart(2, '0')} น.</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={6} sm={3}>
                  <Form.Select 
                    value={checkoutData.custom_minute}
                    onChange={e => setCheckoutData({...checkoutData, custom_minute: e.target.value})}
                  >
                    {['00', '15', '30', '45'].map(m => (
                      <option key={m} value={m}>{m} นาที</option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            </Form.Group>
            
            <Row className="g-2 align-items-end">
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold text-muted small">Payment Method</Form.Label>
                  <Form.Select 
                    value={checkoutData.payment_method}
                    onChange={e => setCheckoutData({...checkoutData, payment_method: e.target.value})}
                  >
                    <option value="transfer">Bank Transfer (โอนเงิน)</option>
                    <option value="cod">Cash on Delivery (COD)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold text-muted small">Payment Status</Form.Label>
                  <Form.Select 
                    value={checkoutData.payment_status}
                    onChange={e => setCheckoutData({...checkoutData, payment_status: e.target.value})}
                  >
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {checkoutData.payment_method === 'transfer' && (
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold text-muted small">Payment Slip</Form.Label>
                    <div>
                      {slipPreviewUrl ? (
                        <ImageViewerButton 
                          imageUrl={slipPreviewUrl}
                          onUpdate={(file) => {
                            setSlipFile(file);
                            setSlipPreviewUrl(URL.createObjectURL(file));
                          }}
                          onDelete={() => {
                            setSlipFile(null);
                            setSlipPreviewUrl('');
                          }}
                          buttonText="ดูรูปสลิป"
                        />
                      ) : (
                        <Form.Label className="btn btn-outline-secondary btn-sm m-0 w-100" style={{ cursor: 'pointer' }}>
                          แนบรูปสลิป
                          <Form.Control 
                            type="file" 
                            accept="image/*" 
                            style={{ display: 'none' }} 
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setSlipFile(file);
                                setSlipPreviewUrl(URL.createObjectURL(file));
                              }
                            }} 
                          />
                        </Form.Label>
                      )}
                    </div>
                  </Form.Group>
                </Col>
              )}
            </Row>
            
            {/* Discounts Section */}
            <div className="mb-3 border rounded-3 p-3 bg-white">
              <Row className="g-2 mb-2 px-1 text-muted small fw-bold text-center align-items-center">
                <Col xs={4} className="text-start">
                  <Form.Label className="fw-bold text-muted small m-0">Discounts (ส่วนลด)</Form.Label>
                </Col>
                <Col xs={2}>ประเภท</Col>
                <Col xs={2}>มูลค่า</Col>
                <Col xs={3}>สุทธิ(บ.)</Col>
                <Col xs={1} className="text-end position-relative">
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    onClick={handleAddDiscountRow}
                    className="p-0 px-1 position-absolute end-0 top-50 translate-middle-y text-nowrap"
                    style={{ fontSize: '11px', height: '24px', lineHeight: '1' }}
                  >
                    <FaPlus /> เพิ่มส่วนลด
                  </Button>
                </Col>
              </Row>
              
              {appliedDiscounts.length === 0 ? (
                <div className="text-muted small text-center py-2">ไม่มีส่วนลดในบิลนี้</div>
              ) : (
                <>
                  {appliedDiscounts.map((disc, idx) => (
                    <Row key={idx} className="g-2 mb-2 align-items-center">
                      <Col xs={4}>
                        <CreatableSelect
                        placeholder="ค้นหา/พิมพ์..."
                        options={masterDiscounts.map(d => ({ value: d.name, label: d.name }))}
                        value={disc.name ? { value: disc.name, label: disc.name } : null}
                        onChange={(selectedOption) => {
                          if (selectedOption) {
                            handleDiscountChange(idx, 'name', selectedOption.value);
                          } else {
                            handleDiscountChange(idx, 'name', '');
                          }
                        }}
                        onCreateOption={(inputValue) => {
                          handleDiscountChange(idx, 'name', inputValue);
                        }}
                        isClearable
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '31px',
                            height: '31px',
                            fontSize: '0.875rem'
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            padding: '0 8px',
                            height: '29px'
                          }),
                          singleValue: (base) => ({
                            ...base,
                            margin: 0
                          }),
                          input: (base) => ({
                            ...base,
                            margin: 0,
                            padding: 0
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: '29px'
                          })
                        }}
                      />
                    </Col>
                    <Col xs={2}>
                      <Form.Select
                        value={disc.type}
                        size="sm"
                        disabled
                        className="bg-light"
                      >
                        <option value="amount">บาท</option>
                        <option value="percentage">%</option>
                      </Form.Select>
                    </Col>
                    <Col xs={2}>
                      <Form.Control
                        type="number"
                        placeholder="มูลค่า..."
                        value={disc.value}
                        size="sm"
                        disabled
                        className="bg-light"
                      />
                    </Col>
                    <Col xs={3}>
                      <Form.Control
                        type="number"
                        placeholder="ส่วนลดสุทธิ..."
                        value={disc.is_override ? disc.net_value : getNetDiscount(disc, calculateSubtotal())}
                        onChange={(e) => {
                          const newDiscounts = [...appliedDiscounts];
                          newDiscounts[idx].is_override = true;
                          newDiscounts[idx].net_value = e.target.value;
                          setAppliedDiscounts(newDiscounts);
                        }}
                        size="sm"
                        min="0"
                        step="0.01"
                        className="fw-bold text-danger bg-white text-center"
                      />
                    </Col>
                    <Col xs={1} className="text-end">
                      <Button variant="link" className="text-danger p-0" onClick={() => handleRemoveDiscountRow(idx)}>
                        <FaTrash />
                      </Button>
                    </Col>
                  </Row>
                ))}
                </>
              )}
            </div>
            
            <div className="bg-light p-3 rounded-3 mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted">Subtotal:</span>
                <span>฿{calculateSubtotal().toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              {appliedDiscounts.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mb-2 text-danger">
                  <span>Discount:</span>
                  <span>- ฿{calculateDiscount(calculateSubtotal()).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
              )}
              <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                <span className="fw-bold text-dark">Total Amount:</span>
                <span className="fs-4 fw-bold" style={{ color: primaryColor }}>
                  ฿{Math.max(0, calculateSubtotal() - calculateDiscount(calculateSubtotal())).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </span>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="light" onClick={() => setShowCheckout(false)}>Cancel</Button>
            <Button type="submit" variant="primary" className="border-0 px-4" style={{ backgroundColor: primaryColor }}>Place Order</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <style>{`
        .product-card:hover {
          box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important;
        }
      `}</style>
    </Container>
    </>
  );
}
